import {
  ComposerConnectorMark,
  ComposerRemoveMark,
} from "./composerConnectors";
import type { HeadlineChip } from "../HeadlinePickerPopover";

export interface ComposerDocumentAttachment {
  id: string;
  filename: string;
  text: string;
}

export interface ComposerNarrativeAttachment {
  id: string;
  label: string;
}

interface ComposerAttachedItemsDrawerProps {
  documents: ComposerDocumentAttachment[];
  imageCount: number;
  headlines?: HeadlineChip[];
  narratives?: ComposerNarrativeAttachment[];
  onRemoveDocument: (id: string) => void;
  onRemoveImage: (index: number) => void;
  onRemoveHeadline?: (id: string) => void;
  onRemoveNarrative?: (id: string) => void;
}

export function ComposerAttachedItemsDrawer({
  documents,
  imageCount,
  headlines = [],
  narratives = [],
  onRemoveDocument,
  onRemoveImage,
  onRemoveHeadline,
  onRemoveNarrative,
}: ComposerAttachedItemsDrawerProps) {
  const imageIndexes = Array.from({ length: imageCount }, (_, index) => index);
  const hasItems =
    documents.length > 0 ||
    imageIndexes.length > 0 ||
    headlines.length > 0 ||
    narratives.length > 0;

  if (!hasItems) return null;

  return (
    <div
      className="fintheon-attachment-bottom-drawer"
      data-attachment-bottom-drawer
      aria-label="Attached context"
    >
      {narratives.map((item) => (
        <AttachedChip
          key={`narrative-${item.id}`}
          title={item.label}
          subtitle="Narrative · session read"
          kind="narrative"
          onRemove={() => onRemoveNarrative?.(item.id)}
        />
      ))}
      {documents.map((item) => (
        <AttachedChip
          key={`document-${item.id}`}
          title={item.filename}
          subtitle="Attachment · document"
          kind="document"
          onRemove={() => onRemoveDocument(item.id)}
        />
      ))}
      {imageIndexes.map((index) => (
        <AttachedChip
          key={`image-${index}`}
          title={`Image ${index + 1}`}
          subtitle="Attachment · media"
          kind="image"
          onRemove={() => onRemoveImage(index)}
        />
      ))}
      {headlines.map((item) => (
        <AttachedChip
          key={`headline-${item.id}`}
          title={item.headline}
          subtitle="Headline · market desk"
          kind="headline"
          onRemove={() => onRemoveHeadline?.(item.id)}
        />
      ))}
    </div>
  );
}

function AttachedChip({
  title,
  subtitle,
  kind,
  onRemove,
}: {
  title: string;
  subtitle: string;
  kind: "document" | "headline" | "image" | "narrative";
  onRemove?: () => void;
}) {
  return (
    <button
      type="button"
      className="fintheon-attachment-chip"
      data-attached-kind={kind}
      onClick={onRemove}
      aria-label={`Remove ${title}`}
    >
      <span className="fintheon-attachment-chip-icon" aria-hidden="true">
        {kind === "narrative" ? (
          <ComposerConnectorMark kind="narrativeflow" className="icon-mark" />
        ) : kind === "headline" ? (
          <HeadlineMark className="icon-mark" />
        ) : kind === "image" ? (
          <ImageMark className="icon-mark" />
        ) : (
          <DocumentMark className="icon-mark" />
        )}
        <ComposerRemoveMark className="icon-remove" />
      </span>
      <span>
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </span>
    </button>
  );
}

function DocumentMark({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M6 5.5h9l3 3V20H6z"
        fill="rgba(199,159,74,0.12)"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        d="M15 5.5v4h4M8.5 12h7M8.5 15.5h7"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function HeadlineMark({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M14 14V6m0 8l6.102 3.487a.6.6 0 0 0 .898-.52V3.033a.6.6 0 0 0-.898-.521L14 6m0 8H7a4 4 0 1 1 0-8h7M7.757 19.3L7 14h4l.677 4.74a1.98 1.98 0 0 1-3.92.56Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function ImageMark({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 256 256" aria-hidden="true">
      <path
        fill="currentColor"
        d="M216 42H40a14 14 0 0 0-14 14v144a14 14 0 0 0 14 14h176a14 14 0 0 0 14-14V56a14 14 0 0 0-14-14M40 54h176a2 2 0 0 1 2 2v107.57l-29.47-29.47a14 14 0 0 0-19.8 0l-21.42 21.42l-45.41-45.42a14 14 0 0 0-19.8 0L38 154.2V56a2 2 0 0 1 2-2m-2 146v-28.83l52.58-52.58a2 2 0 0 1 2.84 0L176.83 202H40a2 2 0 0 1-2-2m178 2h-22.2l-38-38l21.41-21.42a2 2 0 0 1 2.83 0l38 38V200a2 2 0 0 1-2.04 2m-70-102a10 10 0 1 1 10 10a10 10 0 0 1-10-10"
      />
    </svg>
  );
}
